/**
 * Parses the structured shared visitor availability response without relying
 * on its display message to identify the affected ticket selection.
 *
 * @param {object} error
 * @return {{message: string, contexts: object[]}|null}
 */
function parseSharedVisitorTypeUnavailable(error) {
    const payload = error?.response?.data?.errors?.shared_visitor_type_unavailable?.[0];

    if (typeof payload !== 'string') {
        return null;
    }

    try {
        const decoded = JSON.parse(payload);

        if (
            !decoded ||
            typeof decoded.message !== 'string' ||
            decoded.message.trim() === '' ||
            !Array.isArray(decoded.contexts)
        ) {
            return null;
        }

        const contexts = decoded.contexts.filter(context => {
            return context &&
                typeof context.selection_seat_group_id === 'string' &&
                context.selection_seat_group_id !== '' &&
                typeof context.visitor_type === 'string' &&
                context.visitor_type !== '' &&
                typeof context.shared_inventory_family === 'string' &&
                context.shared_inventory_family !== '' &&
                context.seat_type === 'reserved';
        });

        if (contexts.length === 0) {
            return null;
        }

        return {message: decoded.message, contexts};
    } catch (exception) {
        return null;
    }
}

/**
 * Removes only the positive Reserved selection identified by the structured
 * server context. A null result leaves normal error handling in control.
 *
 * @param {object} error
 * @param {GlobalState} state
 * @return {{message: string, contexts: object[]}|null}
 */
function recoverSharedVisitorTypeUnavailable(error, state) {
    const parsed = parseSharedVisitorTypeUnavailable(error);

    if (!parsed || !Array.isArray(state?.reserveTicketCounts)) {
        return null;
    }

    const matchingSelections = new Set(parsed.contexts.map(context => {
        return `${context.selection_seat_group_id}:${context.visitor_type}`;
    }));
    let changed = false;

    const retainedRows = state.reserveTicketCounts.filter(row => {
        const matchesContext = matchingSelections.has(`${row.groupId}:${row.visitorTypeId}`);
        const isPositive = Number(row.count) > 0;

        if (matchesContext && isPositive) {
            changed = true;
            return false;
        }

        return true;
    });

    if (!changed) {
        return null;
    }

    state.setReserveTicketCounts(retainedRows);

    return parsed;
}

export {parseSharedVisitorTypeUnavailable, recoverSharedVisitorTypeUnavailable};
